import React from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, LogOut, Shield, Search, Download, Moon, Sun } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import SystemBanner from "@/components/SystemBanner";
import SuperAdminErrorBoundary from "@/components/superadmin/SuperAdminErrorBoundary";
import { isSuperadminOnly } from "@/lib/superadminOnly";

const NAV = [
    { to: "/superadmin", label: "Overview", end: true, testId: "sa-nav-overview" },
    { to: "/superadmin/tenants", label: "Tenants", testId: "sa-nav-tenants" },
    { to: "/superadmin/analytics", label: "Analytics", testId: "sa-nav-analytics" },
    { to: "/superadmin/billing", label: "Billing", testId: "sa-nav-billing" },
    { to: "/superadmin/releases", label: "Releases", testId: "sa-nav-releases" },
    { to: "/superadmin/desktop-releases", label: "Desktop", testId: "sa-nav-desktop-releases" },
    { to: "/superadmin/preview-soak", label: "Preview Soak", testId: "sa-nav-preview-soak" },
    { to: "/superadmin/security", label: "Security", testId: "sa-nav-security" },
    { to: "/superadmin/alerts", label: "Alerts", testId: "sa-nav-alerts" },
    { to: "/superadmin/audit", label: "Audit Log", testId: "sa-nav-audit" },
    { to: "/superadmin/settings", label: "Settings", testId: "sa-nav-settings" },
];

/**
 * Super Admin shell — fully independent from tenant Layout:
 *  - NO sidebar (full-width content)
 *  - Dark navy canvas with #004AAD→#00C2FF accent chroma
 *  - Manrope type, dense horizontal nav
 *  - Top bar: logo · search · alerts · profile
 */
export default function SuperAdminLayout() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const doLogout = async () => {
        await logout();
        navigate("/superadmin/login", { replace: true });
    };

    const initials = (user?.name || user?.email || "SA")
        .split(/[\s@.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase())
        .join("") || "SA";

    return (
        <div
            className="min-h-screen text-white"
            data-testid="superadmin-shell"
            style={{
                fontFamily: "Manrope, 'Inter var', Inter, system-ui, sans-serif",
                // Dark-navy background by default; index.css remaps this
                // (and the white/NN utilities throughout the console) to
                // light-mode tokens when html:not(.dark) — see the
                // "SuperAdmin light mode" block there.
                background:
                    "radial-gradient(1200px 600px at 10% -10%, rgba(0,74,173,0.22), transparent 60%), radial-gradient(900px 500px at 90% -20%, rgba(0,194,255,0.14), transparent 55%), #050b1c",
            }}
        >
            <SystemBanner />
            {/* Top Nav */}
            <header
                className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-white/5 bg-[#050b1c]/75 px-6 backdrop-blur-xl"
                data-testid="sa-topbar"
            >
                <Link to="/superadmin" className="flex items-center gap-2">
                    <div
                        className="flex h-7 w-7 items-center justify-center rounded-md"
                        style={{ background: "linear-gradient(135deg, #004AAD 0%, #00C2FF 100%)" }}
                    >
                        <Shield size={14} strokeWidth={2.5} />
                    </div>
                    <div className="leading-tight">
                        <div className="text-[13px] font-semibold tracking-tight">MoovBytes</div>
                        <div className="text-[9px] uppercase tracking-[0.22em] text-white/50">
                            Super Admin
                        </div>
                    </div>
                </Link>

                <nav className="ml-4 flex items-center gap-1">
                    {NAV.map((item) => {
                        const active = item.end
                            ? pathname === item.to
                            : pathname.startsWith(item.to);
                        return (
                            <Link
                                key={item.to}
                                to={item.to}
                                data-testid={item.testId}
                                className={[
                                    "relative px-3 py-1.5 text-[12.5px] font-medium transition-colors",
                                    active ? "text-white" : "text-white/55 hover:text-white/85",
                                ].join(" ")}
                            >
                                {item.label}
                                {active && (
                                    <span
                                        className="absolute -bottom-[13px] left-3 right-3 h-[2px] rounded-full"
                                        style={{
                                            background: "linear-gradient(90deg, #004AAD, #00C2FF)",
                                            boxShadow: "0 0 10px rgba(0,194,255,0.7)",
                                        }}
                                    />
                                )}
                            </Link>
                        );
                    })}
                </nav>

                <div className="ml-4 flex min-w-0 flex-1 items-center">
                    <div className="relative w-full max-w-md">
                        <Search
                            size={13}
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35"
                        />
                        <input
                            data-testid="sa-global-search"
                            placeholder="Search tenants, users, devices, jobs…"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && e.currentTarget.value.trim()) {
                                    navigate(
                                        `/superadmin/tenants?q=${encodeURIComponent(e.currentTarget.value.trim())}`,
                                    );
                                }
                            }}
                            className="h-8 w-full rounded-md border border-white/5 bg-white/[0.03] pl-8 pr-3 text-[12.5px] text-white placeholder:text-white/30 focus:border-[#00C2FF]/50 focus:outline-none focus:ring-1 focus:ring-[#00C2FF]/30"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-1">
                    <button
                        data-testid="sa-theme-toggle"
                        onClick={toggle}
                        className="rounded-md p-2 text-white/65 transition-colors hover:bg-white/5 hover:text-white"
                        title="Toggle theme"
                    >
                        {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
                    </button>
                    <Link
                        to="/superadmin/alerts"
                        data-testid="sa-alerts-bell"
                        className="rounded-md p-2 text-white/65 transition-colors hover:bg-white/5 hover:text-white"
                    >
                        <Bell size={14} />
                    </Link>
                    <div className="ml-2 flex items-center gap-2 border-l border-white/10 pl-3">
                        <div
                            className="flex h-7 w-7 items-center justify-center rounded-full text-[10.5px] font-semibold"
                            style={{
                                background: "linear-gradient(135deg, #004AAD 0%, #00C2FF 100%)",
                            }}
                            data-testid="sa-profile-avatar"
                        >
                            {initials}
                        </div>
                        <div className="min-w-0 leading-tight">
                            <div className="truncate text-[11.5px] font-semibold">
                                {user?.name || "Super Admin"}
                            </div>
                            <div className="truncate text-[9px] uppercase tracking-wider text-[#00C2FF]/80">
                                superadmin
                            </div>
                        </div>
                        <button
                            data-testid="sa-logout"
                            onClick={doLogout}
                            className="ml-2 rounded-md p-1.5 text-white/55 transition-colors hover:bg-red-500/15 hover:text-red-300"
                            title="Sign out"
                        >
                            <LogOut size={14} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Exit to tenant UI — superadmins can still use their home-tenant dashboard */}
            <div className="flex items-center justify-between border-b border-white/5 px-6 py-2 text-[11px] text-white/45">
                <div>
                    Last refresh:{" "}
                    <span className="text-white/70">{new Date().toLocaleTimeString()}</span>
                </div>
                <div className="flex items-center gap-3">
                    {/* /dashboard doesn't exist on a standalone superadmin-only
                        deployment (NotOnSuperadminOnly bounces it back here),
                        so the link would just be a broken promise there. */}
                    {!isSuperadminOnly() && (
                        <Link
                            to="/dashboard"
                            className="text-[#00C2FF] hover:underline"
                            data-testid="sa-exit-to-tenant"
                        >
                            ← Back to tenant dashboard
                        </Link>
                    )}
                </div>
            </div>

            {/* Content */}
            <main className="mx-auto w-full max-w-[1560px] px-6 pb-16 pt-6">
                <SuperAdminErrorBoundary>
                    <Outlet />
                </SuperAdminErrorBoundary>
            </main>
        </div>
    );
}
